import React from "react";
import type { ComponentProps } from "./index";
import { useWorkspaceJson } from "./useWorkspaceJson";
import { asStringArray, filterByIds, isUrlValue, normalizeRows, pickColumns, rowId } from "./tableData";

/**
 * Renders `{ source, ids?, columns? }` as a table.
 *
 * `source` is a workspace-relative path to a JSON file; `ids`/`columns` are references into it,
 * not the row data itself — the model says which rows and which columns, this component fetches
 * what they actually contain. That is what keeps a match score or an apply URL from being
 * retyped by the model on its way to the screen.
 */
export function Table({ sessionId, args }: ComponentProps): React.ReactNode {
	const source = typeof args.source === "string" ? args.source : undefined;
	const ids = asStringArray(args.ids);
	const columns = asStringArray(args.columns);
	// Which row field carries the expandable detail, if any. A row without it
	// gets no disclosure control, so a table whose rows mostly have nothing to
	// show does not grow a column of empty triangles.
	const detailsField = typeof args.details === "string" ? args.details : undefined;
	const [expanded, setExpanded] = React.useState<Set<string>>(() => new Set());

	const { data, error, loading } = useWorkspaceJson<unknown>(sessionId, source);

	if (!source) {
		return <div className="component error">table 组件缺少 source 参数，收到的参数：{JSON.stringify(args)}</div>;
	}
	if (loading) return <TableSkeleton />;
	if (error) return <div className="component error">{error}</div>;

	const rows = normalizeRows(data);
	if (rows === null) {
		return (
			<div className="component error">
				{source} 不是表格数据：既非数组，也无 items 字段
			</div>
		);
	}

	const filtered = filterByIds(rows, ids);
	if (filtered.length === 0) {
		return (
			<div className="component">
				<p className="hint">无匹配行</p>
			</div>
		);
	}

	const cols = pickColumns(filtered, columns).filter((c) => c !== detailsField);
	return (
		<div className="component table-component">
			<div className="table-wrap">
				<table className="data-table">
					<thead>
						<tr>
							{cols.map((c) => (
								<th key={c}>{c}</th>
							))}
						</tr>
					</thead>
					<tbody>
						{filtered.map((row, i) => {
							const id = rowId(row, i);
							const detail = detailsField ? cellText(row[detailsField]) : "";
							const isOpen = expanded.has(id);
							return (
								<React.Fragment key={id}>
									<tr>
										{cols.map((c, n) => (
											<td key={c} data-label={c} className={n === 0 ? "cell-primary" : undefined} title={cellText(row[c])}>
												{n === 0 && detail ? (
													<button
														type="button"
														className="row-toggle"
														aria-expanded={isOpen}
														onClick={() =>
															setExpanded((prev) => {
																const next = new Set(prev);
																next.has(id) ? next.delete(id) : next.add(id);
																return next;
															})
														}
													>
														{isOpen ? "▾" : "▸"}
													</button>
												) : null}
												{renderCell(row[c])}
											</td>
										))}
									</tr>
									{isOpen && detail ? (
										<tr className="row-detail">
											<td colSpan={cols.length}>{detail}</td>
										</tr>
									) : null}
								</React.Fragment>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
}

function cellText(value: unknown): string {
	if (value === null || value === undefined) return "";
	return typeof value === "object" ? JSON.stringify(value) : String(value);
}

function renderCell(value: unknown): React.ReactNode {
	if (value === null || value === undefined) return "";
	if (isUrlValue(value)) {
		return (
			<a href={value} target="_blank" rel="noreferrer noopener">
				{value}
			</a>
		);
	}
	return cellText(value);
}

function TableSkeleton() {
	return (
		<div className="component table-skeleton" aria-hidden="true">
			<div className="skeleton-row" />
			<div className="skeleton-row" />
			<div className="skeleton-row" />
		</div>
	);
}
